import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Radar, Users, FileText, CheckSquare, TrendingUp, Target, Inbox,
  GraduationCap, FolderOpen, RefreshCw,
} from 'lucide-react';
import EmptyState from '../components/EmptyState';
import MeetingsTab from '../components/BDAssistant/MeetingsTab';
import DebriefsTab from '../components/BDAssistant/DebriefsTab';
import CoachTab from '../components/BDAssistant/CoachTab';
import RadarTab from '../components/BDAssistant/RadarTab';
import ContentStudioTab from '../components/BDAssistant/ContentStudioTab';
import ExpertiseTab from '../components/BDAssistant/ExpertiseTab';
import PipelineTab from '../components/BDAssistant/PipelineTab';
import AuditHistoryPanel from '../components/BDApprovals/AuditHistoryPanel';
import AdoptionPanel from '../components/BD/AdoptionPanel';
import ActivityFeedPanel from '../components/BD/ActivityFeedPanel';
import BDApprovals from './BDApprovals';
import BDEngagements from './BDEngagements';
import { useAuth } from '../contexts/AuthContext';
import { useBdAssistantEligibility } from '../hooks/useBdAssistantEligibility';
import { useBdStepSwitches, stepOffNote } from '../hooks/useBdStepSwitches';

/**
 * BD-04 — the Business Development module shell. Hand-rolled tab bar,
 * cloned from ProjectManagement/PMDashboard.tsx's exact pattern (no
 * reusable TabBar component exists anywhere in this codebase). Only two
 * tabs have real content: Approvals (BD-03, embedded unchanged) and
 * Engagements (the existing BD Pipeline list, embedded unchanged). The
 * other 8 are empty-state shells for later tickets — see "Not in scope" in
 * the BD-04 ticket.
 */

type TabType =
  | 'radar' | 'meetings' | 'debriefs' | 'approvals' | 'pipeline' | 'engagements' | 'activity_feed'
  | 'coach' | 'content_studio';

const TABS: { value: TabType; label: string; icon: React.FC<{ className?: string }> }[] = [
  { value: 'radar', label: '1. Radar', icon: Radar },
  { value: 'meetings', label: '2. Meetings', icon: Users },
  { value: 'debriefs', label: '3. Debriefs', icon: FileText },
  { value: 'approvals', label: '4. Approvals', icon: CheckSquare },
  { value: 'pipeline', label: '5. Pipeline', icon: TrendingUp },
  { value: 'engagements', label: '6. Engagements', icon: Target },
  { value: 'activity_feed', label: '7. Activity Feed', icon: Inbox },
  { value: 'coach', label: 'Coach', icon: GraduationCap },
  { value: 'content_studio', label: 'Content Studio', icon: FolderOpen },
];

const parseTabFromSearch = (search: string): TabType | null => {
  const p = new URLSearchParams(search);
  const t = p.get('tab') as TabType | null;
  return t && TABS.some((tab) => tab.value === t) ? t : null;
};

const BusinessDevelopment: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ['ADMIN', 'MASTER', 'SUPER_ADMIN'].includes(user?.role || '');
  // The activity feed's "everyone" view follows the backend's BD admin rule (bdVisibility).
  const isBdAdmin = ['ADMIN', 'SUPER_ADMIN'].includes(user?.role || '');
  const { allowed: bdEligible, loading: bdEligibilityLoading } = useBdAssistantEligibility();
  const stepSwitches = useBdStepSwitches();

  const [activeTab, setActiveTab] = useState<TabType>(
    () => parseTabFromSearch(location.search) || parseTabFromSearch(window.location.search) || 'radar'
  );

  // Follow the URL: a link to ?tab=… from inside a tab (e.g. Pipeline -> Approvals)
  // or the browser's back/forward changes the tab, not just the first load.
  useEffect(() => {
    const t = parseTabFromSearch(location.search);
    if (t) setActiveTab((current) => (current === t ? current : t));
  }, [location.search]);

  const selectTab = (tab: TabType) => {
    setActiveTab(tab);
    const params = new URLSearchParams(location.search);
    if (params.get('tab') !== tab) {
      params.set('tab', tab);
      navigate({ pathname: location.pathname, search: `?${params.toString()}` });
    }
  };

  const renderTabBody = () => {
    switch (activeTab) {
      case 'approvals':
        return <BDApprovals />;
      case 'engagements':
        return <BDEngagements />;
      case 'radar':
        return <RadarTab />;
      case 'meetings':
        return <MeetingsTab />;
      case 'debriefs':
        return <DebriefsTab />;
      case 'pipeline':
        return (
          <>
            {stepOffNote(stepSwitches, 'pipeline_review') && (
              <p className="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="bd-pipeline-step-off">
                {stepOffNote(stepSwitches, 'pipeline_review').trim()}
              </p>
            )}
            <PipelineTab />
          </>
        );
      case 'activity_feed':
        // BD-11: everyone gets their own timeline; the adoption and audit panels stay admin-only.
        return isAdmin
          ? <div className="space-y-6"><AdoptionPanel /><ActivityFeedPanel isAdmin={isBdAdmin} /><AuditHistoryPanel /></div>
          : <ActivityFeedPanel isAdmin={false} />;
      case 'coach':
        return <CoachTab />;
      case 'content_studio':
        return <ContentStudioTab />;
//         return <ExpertiseTab />;
      default:
        return null;
    }
  };

  // The nav hides this page for people who are not switched on; this covers a
  // direct URL. The APIs enforce the same flag server-side either way.
  if (bdEligibilityLoading) return <div className="max-w-6xl mx-auto p-6" />;
  if (!bdEligible) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <EmptyState
          icon={Radar}
          title="Business Development is not switched on for you"
          description="An admin turns it on from Settings → BD Assistant. Only Stemmler email addresses can be switched on."
        />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Business Development</h1>
        </div>
        <div className="flex items-center gap-2">
          <select className="text-sm border rounded px-3 py-1.5 text-gray-600" disabled title="Coming soon">
            <option>All Accounts</option>
          </select>
          <div className="flex text-sm border rounded overflow-hidden">
            <button className="px-3 py-1.5 bg-blue-50 text-blue-700" disabled>My</button>
            <button className="px-3 py-1.5 text-gray-500" disabled>Firm</button>
          </div>
          <button className="p-2 rounded hover:bg-gray-100 text-gray-500" title="Refresh" onClick={() => window.location.reload()}>
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <div className="flex flex-wrap">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => selectTab(tab.value)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.value
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {renderTabBody()}
    </div>
  );
};

export default BusinessDevelopment;
